<template>
  <transition name="modal-fade">
    <div v-if="visible" class="modal-mask" @click.self="handleClose">
      <div class="modal-panel" role="dialog" aria-modal="true">
        <button type="button" class="close-btn" aria-label="关闭" @click="handleClose">
          <i class="bi bi-x-lg"></i>
        </button>

        <!-- 顶栏 -->
        <div class="modal-header-bg"></div>
        <div class="modal-user-row">
          <div class="user-avatar">{{ avatarText }}</div>
          <div class="user-meta">
            <div class="user-name">{{ userName }}</div>
            <div class="user-points">
              剩余点数：
              <strong class="balance">{{ formattedBalance }}</strong>
            </div>
          </div>
          <button
            type="button"
            class="ledger-redeem-btn"
            @click="$emit('open-redeem')">
            <i class="bi bi-gift"></i> 兑换码
          </button>
          <button
            v-if="view === 'plans'"
            type="button"
            class="ledger-link"
            @click="view = 'ledger'">
            点数明细
          </button>
        </div>

        <!-- 套餐列表 -->
        <div v-show="view === 'plans'" class="modal-body">
          <div v-if="loadingPackages" class="packages-loading">套餐加载中...</div>
          <div v-else-if="!packages.length" class="packages-empty">暂无可用套餐</div>
          <div v-else class="plan-grid">
            <div
              v-for="pkg in packages"
              :key="pkg.productId"
              class="plan-card-wrap">
              <div
                class="plan-card"
                :class="{ active: selectedId === pkg.productId, featured: pkg.featured }"
                @click="selectPlan(pkg)">
                <span v-if="pkg.badge" class="plan-badge">{{ pkg.badge }}</span>
                <h3>{{ pkg.name }}</h3>
                <div class="plan-price">
                  <span class="amount">{{ formatPrice(pkg.price) }}</span>
                  <span class="unit">元</span>
                  <span v-if="pkg.originalPrice" class="origin">¥{{ pkg.originalPrice }}</span>
                </div>
                <div class="plan-points">
                  <span class="label">通用点数</span>
                  <span class="value">{{ pkg.quotaAmount.toLocaleString() }}</span>
                </div>
                <span class="per-thousand">{{ pkg.perThousand }}</span>
                <p class="plan-desc">{{ pkg.description }}</p>
                <button
                  type="button"
                  class="select-btn"
                  :class="{ primary: selectedId === pkg.productId }"
                  @click.stop="selectPlan(pkg)">
                  {{ selectedId === pkg.productId ? '已选择' : '选择此方案' }}
                </button>
              </div>
            </div>
          </div>

          <div class="rules-block">
            <h4>通用点数说明 <span class="warn">（可用于降重、降AIGC、双降）</span></h4>
            <div v-for="rule in rules" :key="rule.title" class="rule-item">
              <p class="rule-title">{{ rule.title }}</p>
              <p class="rule-desc">{{ rule.desc }}</p>
              <p class="rule-example">{{ rule.example }}</p>
            </div>
          </div>

          <div v-if="payError" class="pay-error">{{ payError }}</div>
          <div v-if="selectedPlan" class="pay-bar">
            <div class="pay-summary">
              <span>已选：{{ selectedPlan.name }}</span>
              <span class="pay-points">+{{ selectedPlan.quotaAmount.toLocaleString() }} 点</span>
              <span class="pay-price">¥{{ formatPrice(selectedPlan.price) }}</span>
            </div>
            <button
              v-if="wechatEnabled"
              type="button"
              class="pay-btn"
              :disabled="paying"
              @click="handleWeChatPay">
              {{ paying ? '正在下单...' : '微信支付' }}
            </button>
            <button
              v-if="alipayEnabled"
              type="button"
              class="pay-btn pay-btn-alipay"
              :disabled="paying"
              @click="handleAlipayPay">
              {{ paying ? '正在下单...' : '支付宝支付' }}
            </button>
            <button
              v-if="mockEnabled"
              type="button"
              class="pay-btn pay-btn-mock"
              :disabled="paying"
              @click="handleMockPay">
              {{ paying ? '支付中...' : '模拟支付（开发）' }}
            </button>
          </div>
        </div>

        <!-- 微信扫码支付 -->
        <div v-show="view === 'pay'" class="modal-body pay-step">
          <button type="button" class="back-link" @click="cancelPay">← 返回选择套餐</button>
          <div class="pay-step-title">{{ payStepTitle }}</div>
          <div v-if="paying && !codeUrl" class="packages-loading">正在生成支付二维码...</div>
          <template v-else-if="codeUrl">
            <div class="qrcode-wrap">
              <img :src="qrImageUrl" alt="支付二维码" class="qrcode-img" width="220" height="220">
            </div>
            <p class="pay-hint">{{ payHint }}</p>
            <p class="pay-amount">支付金额：<strong>¥{{ formatPrice(selectedPlan ? selectedPlan.price : 0) }}</strong></p>
            <p v-if="polling" class="pay-waiting">等待支付结果...</p>
          </template>
        </div>

        <div v-show="view === 'ledger'" class="modal-body">
          <PointsLedgerPanel
            :visible="view === 'ledger'"
            :records="ledgerRecords"
            @back="view = 'plans'"
            @open-redeem="$emit('open-redeem')" />
        </div>

        <transition name="toast-fade">
          <div v-if="successTip" class="success-toast">
            <i class="bi bi-check-circle-fill"></i>
            充值成功，到账 {{ lastRechargePoints.toLocaleString() }} 点
          </div>
        </transition>
      </div>
    </div>
  </transition>
</template>

<script>
import { POINTS_RULES } from '@/constants/rechargePackages'
import { getProductList } from '@/api/portal/product'
import { createOrder, mockPayOrder } from '@/api/portal/order'
import { getPaymentConfig, createPay, getOrderStatus } from '@/api/portal/pay'
import PointsLedgerPanel from '@/components/recharge/PointsLedgerPanel.vue'

const PAY_CHANNEL_WECHAT = 'WECHAT_NATIVE'
const PAY_CHANNEL_ALIPAY = 'ALIPAY_PC'
const POLL_INTERVAL_MS = 2000
const POLL_MAX_TIMES = 150

export default {
  name: 'RechargeModal',
  components: { PointsLedgerPanel },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    balance: {
      type: Number,
      default: 0
    },
    userName: {
      type: String,
      default: '用户'
    },
    ledgerRecords: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      view: 'plans',
      packages: [],
      rules: POINTS_RULES,
      selectedId: null,
      loadingPackages: false,
      paying: false,
      successTip: false,
      lastRechargePoints: 0,
      payError: '',
      mockEnabled: false,
      wechatEnabled: false,
      alipayEnabled: false,
      currentOrderNo: '',
      currentPayChannel: '',
      codeUrl: '',
      polling: false,
      pollTimer: null,
      pollCount: 0
    }
  },
  computed: {
    formattedBalance() {
      return this.balance.toLocaleString()
    },
    avatarText() {
      return (this.userName || 'U').charAt(0).toUpperCase()
    },
    selectedPlan() {
      return this.packages.find(p => p.productId === this.selectedId) || null
    },
    qrImageUrl() {
      if (!this.codeUrl) return ''
      return `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(this.codeUrl)}`
    },
    payStepTitle() {
      return this.currentPayChannel === PAY_CHANNEL_ALIPAY ? '支付宝扫码支付' : '微信扫码支付'
    },
    payHint() {
      return this.currentPayChannel === PAY_CHANNEL_ALIPAY
        ? '请使用支付宝扫一扫完成支付'
        : '请使用微信扫一扫完成支付'
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.view = 'plans'
        this.paying = false
        this.successTip = false
        this.payError = ''
        this.currentOrderNo = ''
        this.currentPayChannel = ''
        this.codeUrl = ''
        this.stopPolling()
        this.loadProducts()
        this.loadPaymentConfig()
      } else {
        this.stopPolling()
      }
    }
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    loadProducts() {
      this.loadingPackages = true
      getProductList()
        .then(res => {
          this.packages = res.data || []
          const featured = this.packages.find(p => p.featured)
          this.selectedId = featured
            ? featured.productId
            : (this.packages[0] ? this.packages[0].productId : null)
        })
        .catch(err => {
          this.packages = []
          this.selectedId = null
          this.payError = err.message || '套餐加载失败'
        })
        .finally(() => {
          this.loadingPackages = false
        })
    },
    loadPaymentConfig() {
      getPaymentConfig()
        .then(res => {
          const cfg = res.data || {}
          this.mockEnabled = !!cfg.mockEnabled
          this.wechatEnabled = !!cfg.wechatEnabled
          this.alipayEnabled = !!cfg.alipayEnabled
        })
        .catch(() => {
          this.mockEnabled = false
          this.wechatEnabled = false
          this.alipayEnabled = false
        })
    },
    formatPrice(price) {
      const num = Number(price)
      if (Number.isNaN(num)) return '0'
      return num % 1 === 0 ? String(num) : num.toFixed(2).replace(/\.?0+$/, '')
    },
    selectPlan(pkg) {
      this.selectedId = pkg.productId
      this.payError = ''
    },
    handleClose() {
      this.stopPolling()
      this.$emit('close')
    },
    cancelPay() {
      this.stopPolling()
      this.view = 'plans'
      this.paying = false
      this.codeUrl = ''
      this.currentOrderNo = ''
      this.currentPayChannel = ''
      this.payError = ''
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
      this.polling = false
      this.pollCount = 0
    },
    startPolling() {
      this.stopPolling()
      this.polling = true
      this.pollCount = 0
      this.pollTimer = setInterval(() => {
        this.pollCount += 1
        if (this.pollCount > POLL_MAX_TIMES) {
          this.stopPolling()
          this.payError = '支付超时，如已付款请稍后刷新余额或联系客服'
          return
        }
        getOrderStatus(this.currentOrderNo, this.currentPayChannel)
          .then(res => {
            const data = res.data || {}
            if (data.paid) {
              this.stopPolling()
              this.onPaySuccess({
                orderNo: this.currentOrderNo,
                quotaAmount: data.quotaAmount,
                balance: data.balance,
                available: data.available
              })
            }
          })
          .catch(() => {})
      }, POLL_INTERVAL_MS)
    },
    onPaySuccess(payData) {
      this.lastRechargePoints = payData.quotaAmount || 0
      this.successTip = true
      this.$emit('recharge-success', payData)
      setTimeout(() => {
        this.successTip = false
        this.handleClose()
      }, 1200)
    },
    handleWeChatPay() {
      this.handlePay(PAY_CHANNEL_WECHAT)
    },
    handleAlipayPay() {
      this.handlePay(PAY_CHANNEL_ALIPAY)
    },
    handlePay(channel) {
      if (!channel) {
        this.payError = '支付方式无效'
        return
      }
      if (!this.selectedPlan || this.paying) return
      this.paying = true
      this.payError = ''
      this.codeUrl = ''
      this.currentPayChannel = channel
      this.view = 'pay'
      createOrder({ productId: this.selectedPlan.productId })
        .then(createRes => {
          const orderNo = createRes.data && createRes.data.orderNo
          if (!orderNo) {
            throw new Error('创建订单失败：未返回订单号')
          }
          this.currentOrderNo = orderNo
          return createPay({ orderNo, channel })
        })
        .then(payRes => {
          this.codeUrl = payRes.data.codeUrl || ''
          if (!this.codeUrl) {
            throw new Error('未获取到支付二维码')
          }
          this.startPolling()
        })
        .catch(err => {
          this.view = 'plans'
          this.currentPayChannel = ''
          this.payError = err.message || '发起支付失败，请稍后重试'
        })
        .finally(() => {
          this.paying = false
        })
    },
    handleMockPay() {
      if (!this.selectedPlan || this.paying) return
      this.paying = true
      this.payError = ''
      createOrder({ productId: this.selectedPlan.productId })
        .then(createRes => mockPayOrder({ orderNo: createRes.data.orderNo }))
        .then(payRes => {
          this.onPaySuccess(payRes.data)
        })
        .catch(err => {
          this.payError = err.message || '支付失败，请稍后重试'
        })
        .finally(() => {
          this.paying = false
        })
    }
  }
}
</script>

<style scoped>
.modal-mask {
  position: fixed;
  inset: 0;
  z-index: 1060;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.modal-panel {
  position: relative;
  width: 100%;
  max-width: 898px;
  max-height: 96vh;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
}

.close-btn {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 2;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.8);
}

.modal-header-bg {
  height: 80px;
  background: linear-gradient(135deg, #e3f2fd 0%, #f0f7ff 50%, #ede9fe 100%);
}

.modal-user-row {
  position: relative;
  margin-top: -56px;
  padding: 0 24px 16px;
  display: flex;
  align-items: flex-end;
  gap: 12px;
}

.user-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1976d2, #42a5f5);
  color: #fff;
  font-weight: 700;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.35);
  flex-shrink: 0;
}

.user-meta {
  flex: 1;
  min-width: 0;
}

.user-name {
  font-size: 16px;
  font-weight: 600;
  color: #312e81;
}

.user-points {
  margin-top: 4px;
  font-size: 14px;
  color: #999;
}

.user-points .balance {
  color: var(--primary);
  font-size: 16px;
}

.ledger-link {
  color: var(--primary);
  font-size: 14px;
  white-space: nowrap;
}

.ledger-redeem-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--primary);
  font-size: 14px;
  white-space: nowrap;
  margin-left: auto;
}

.ledger-redeem-btn:hover,
.ledger-link:hover {
  text-decoration: underline;
}

.modal-body {
  padding: 8px 24px 24px;
  overflow-y: auto;
  flex: 1;
}

.plan-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.plan-card-wrap {
  flex: 0 0 calc((100% - 24px) / 3);
  max-width: calc((100% - 24px) / 3);
}

.plan-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  padding: 16px 14px 14px;
  border: 2px solid #e0e7ff;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.plan-card:hover {
  box-shadow: 0 8px 24px rgba(25, 118, 210, 0.1);
}

.plan-card.active,
.plan-card.featured.active {
  border-color: var(--primary);
  box-shadow: 0 8px 28px rgba(25, 118, 210, 0.15);
}

.plan-card.featured {
  border-color: #90caf9;
}

.plan-badge {
  position: absolute;
  top: -10px;
  right: 0;
  padding: 4px 12px;
  border-radius: 999px;
  background: linear-gradient(135deg, #1976d2, #42a5f5);
  color: #fff;
  font-size: 11px;
  white-space: nowrap;
}

.plan-card h3 {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 600;
}

.plan-price {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 2px;
  margin-bottom: 4px;
}

.plan-price .amount {
  font-size: 32px;
  line-height: 1;
  font-weight: 600;
}

.plan-price .unit {
  font-size: 14px;
  margin-bottom: 4px;
}

.plan-price .origin {
  margin-left: 8px;
  margin-bottom: 4px;
  font-size: 13px;
  color: #999;
  text-decoration: line-through;
}

.plan-points {
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin-bottom: 2px;
}

.plan-points .label {
  font-size: 12px;
  color: #666;
}

.plan-points .value {
  font-size: 18px;
  font-weight: 600;
}

.per-thousand {
  font-size: 12px;
  color: #999;
  margin-bottom: 6px;
}

.plan-desc {
  margin: 0 0 10px;
  font-size: 12px;
  color: var(--text-muted);
  text-align: center;
  min-height: 32px;
}

.select-btn {
  width: 100%;
  max-width: 180px;
  height: 32px;
  margin-top: auto;
  border-radius: 8px;
  background: #ede9fe;
  color: var(--primary);
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s;
}

.select-btn.primary {
  background: linear-gradient(135deg, #1976d2, #42a5f5);
  color: #fff;
}

.rules-block {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;
}

.rules-block h4 {
  margin: 0 0 12px;
  font-size: 16px;
  font-weight: 600;
}

.rules-block .warn {
  font-size: 13px;
  color: #ed4848;
  font-weight: 400;
}

.rule-item {
  margin-bottom: 12px;
}

.rule-title {
  margin: 0;
  font-size: 14px;
  color: #666;
}

.rule-desc {
  margin: 4px 0 0;
  font-size: 14px;
  color: #666;
}

.rule-example {
  margin: 2px 0 0;
  font-size: 12px;
  color: #999;
}

.packages-loading,
.packages-empty {
  padding: 48px 0;
  text-align: center;
  color: var(--text-secondary);
  font-size: 14px;
}

.pay-error {
  margin-top: 16px;
  padding: 10px 14px;
  border-radius: 8px;
  background: #fef2f2;
  color: #dc2626;
  font-size: 13px;
}

.pay-bar {
  position: sticky;
  bottom: 0;
  margin-top: 20px;
  padding: 16px 20px;
  border-radius: 12px;
  background: linear-gradient(135deg, #f0f7ff, #e3f2fd);
  border: 1px solid #e0e7ff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.pay-summary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  font-size: 14px;
}

.pay-points {
  color: #059669;
  font-weight: 600;
}

.pay-price {
  font-size: 20px;
  font-weight: 700;
  color: var(--primary);
}

.pay-btn {
  height: 40px;
  padding: 0 24px;
  border-radius: 10px;
  background: linear-gradient(135deg, #1976d2, #42a5f5);
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  box-shadow: 0 8px 20px rgba(25, 118, 210, 0.3);
  transition: opacity 0.2s;
}

.pay-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.pay-btn-mock {
  background: #fff;
  color: var(--primary);
  border: 1px solid #90caf9;
  box-shadow: none;
}

.pay-btn-alipay {
  background: linear-gradient(135deg, #1677ff, #4096ff);
  box-shadow: 0 8px 20px rgba(22, 119, 255, 0.28);
}

.pay-step {
  text-align: center;
  padding-top: 16px;
}

.back-link {
  display: inline-block;
  margin-bottom: 12px;
  color: var(--primary);
  font-size: 14px;
}

.pay-step-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 20px;
}

.qrcode-wrap {
  display: inline-block;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
}

.qrcode-img {
  display: block;
}

.pay-hint {
  margin: 16px 0 8px;
  color: #666;
  font-size: 14px;
}

.pay-amount {
  margin: 0;
  font-size: 14px;
}

.pay-waiting {
  margin-top: 12px;
  color: var(--primary);
  font-size: 13px;
}

.success-toast {
  position: absolute;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 999px;
  background: #059669;
  color: #fff;
  font-size: 14px;
  box-shadow: 0 8px 24px rgba(5, 150, 105, 0.35);
  z-index: 3;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

.toast-fade-enter-active,
.toast-fade-leave-active {
  transition: opacity 0.3s ease;
}

@media (max-width: 860px) {
  .plan-card-wrap {
    flex: 0 0 calc((100% - 12px) / 2);
    max-width: calc((100% - 12px) / 2);
  }
}

@media (max-width: 560px) {
  .plan-card-wrap {
    flex: 0 0 100%;
    max-width: 100%;
  }

  .pay-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .pay-btn {
    width: 100%;
  }
}
</style>
